import { useState, useEffect, useRef } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Tabs, Tab, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { voicemailApi, recordingsApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface VoicemailSettings {
  id: string
  extension_id: string
  mailbox: string
  context: string
  password: string
  fullname: string
  email: string
  email_attach: boolean
  email_delete: boolean
  email_format: string
  unavail_greeting_id: string
  busy_greeting_id: string
  temp_greeting_id: string
  max_message_length: number
  min_message_length: number
  max_messages: number
  say_caller_id: boolean
  say_duration: boolean
  envelope: boolean
  operator: boolean
  is_active: boolean
  extension?: {
    extension: string
    name: string
  }
}

interface VoicemailMessage {
  id: string
  message_id: string
  folder: string
  caller_id: string
  caller_id_name: string
  duration: number
  timestamp: string
  is_read: boolean
  is_urgent: boolean
  file_path?: string
}

const emailFormats = [
  { value: 'wav', label: 'WAV' },
  { value: 'wav49', label: 'WAV49 (komprimiert)' },
  { value: 'gsm', label: 'GSM' },
  { value: 'mp3', label: 'MP3' }
]

const folders = [
  { value: 'INBOX', label: 'Eingang' },
  { value: 'Old', label: 'Alt' },
  { value: 'Work', label: 'Arbeit' },
  { value: 'Family', label: 'Familie' },
  { value: 'Friends', label: 'Freunde' }
]

export default function Voicemail() {
  const confirm = useConfirm()
  const [voicemails, setVoicemails] = useState<VoicemailSettings[]>([])
  const [loading, setLoading] = useState(true)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showMessagesModal, setShowMessagesModal] = useState(false)
  const [selectedVm, setSelectedVm] = useState<VoicemailSettings | null>(null)
  const [messages, setMessages] = useState<VoicemailMessage[]>([])
  const [selectedFolder, setSelectedFolder] = useState('INBOX')
  const [formData, setFormData] = useState({
    password: '',
    email: '',
    email_attach: true,
    email_delete: false,
    email_format: 'wav',
    max_message_length: 180,
    min_message_length: 3,
    max_messages: 100,
    say_caller_id: true,
    say_duration: true,
    envelope: true,
    operator: false,
    unavail_greeting_id: '',
    busy_greeting_id: '',
    temp_greeting_id: ''
  })
  const [recordings, setRecordings] = useState<{id: string, name: string}[]>([])
  const [playingMsgId, setPlayingMsgId] = useState<string | null>(null)
  const [sendingEmail, setSendingEmail] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    loadVoicemails()
    loadRecordings()
  }, [])

  const loadVoicemails = async () => {
    try {
      const response = await voicemailApi.list()
      setVoicemails(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadRecordings = async () => {
    try {
      const response = await recordingsApi.list()
      setRecordings(response.data.items || [])
    } catch {
      console.error('Error loading recordings')
    }
  }

  const openSettingsModal = (vm: VoicemailSettings) => {
    setSelectedVm(vm)
    setFormData({
      password: '',
      email: vm.email || '',
      email_attach: vm.email_attach ?? true,
      email_delete: vm.email_delete ?? false,
      email_format: vm.email_format || 'wav',
      max_message_length: vm.max_message_length || 180,
      min_message_length: vm.min_message_length || 3,
      max_messages: vm.max_messages || 100,
      say_caller_id: vm.say_caller_id ?? true,
      say_duration: vm.say_duration ?? true,
      envelope: vm.envelope ?? true,
      operator: vm.operator ?? false,
      unavail_greeting_id: vm.unavail_greeting_id || '',
      busy_greeting_id: vm.busy_greeting_id || '',
      temp_greeting_id: vm.temp_greeting_id || ''
    })
    setShowSettingsModal(true)
  }

  const openMessagesModal = async (vm: VoicemailSettings) => {
    setSelectedVm(vm)
    setSelectedFolder('INBOX')
    try {
      const response = await voicemailApi.getMessages(vm.id, 'INBOX')
      setMessages(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden der Nachrichten')
    }
    setShowMessagesModal(true)
  }

  const loadMessages = async (folder: string) => {
    if (!selectedVm) return
    setSelectedFolder(folder)
    try {
      const response = await voicemailApi.getMessages(selectedVm.id, folder)
      setMessages(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden der Nachrichten')
    }
  }

  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedVm) return
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.password) delete submitData.password
      if (!submitData.unavail_greeting_id) delete submitData.unavail_greeting_id
      if (!submitData.busy_greeting_id) delete submitData.busy_greeting_id
      if (!submitData.temp_greeting_id) delete submitData.temp_greeting_id

      await voicemailApi.update(selectedVm.id, submitData)
      toast.success('Einstellungen gespeichert')
      setShowSettingsModal(false)
      loadVoicemails()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDeleteMessage = async (msg: VoicemailMessage) => {
    if (!selectedVm) return
    const confirmed = await confirm({
      title: 'Nachricht löschen',
      message: 'Nachricht wirklich löschen?',
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await voicemailApi.deleteMessage(selectedVm.id, msg.message_id, selectedFolder)
      toast.success('Nachricht gelöscht')
      loadMessages(selectedFolder)
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const handlePlayMessage = async (msg: VoicemailMessage) => {
    if (!selectedVm) return

    // If already playing this message, stop it
    if (playingMsgId === msg.message_id) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingMsgId(null)
      return
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }

    setPlayingMsgId(msg.message_id)

    try {
      // Fetch audio with authentication
      const response = await fetch(`/api/voicemail/${selectedVm.id}/messages/${msg.message_id}/audio`, {
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.token || ''}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load audio')
      }

      const blob = await response.blob()
      const audioUrl = URL.createObjectURL(blob)

      const audio = new Audio(audioUrl)
      audioRef.current = audio

      audio.onended = () => {
        setPlayingMsgId(null)
        audioRef.current = null
        URL.revokeObjectURL(audioUrl)
      }

      audio.onerror = () => {
        toast.error('Fehler beim Abspielen')
        setPlayingMsgId(null)
        audioRef.current = null
        URL.revokeObjectURL(audioUrl)
      }

      await audio.play()
    } catch {
      toast.error('Fehler beim Abspielen')
      setPlayingMsgId(null)
      audioRef.current = null
    }
  }

  const handleResendEmail = async (msg: VoicemailMessage) => {
    if (!selectedVm) return

    if (!selectedVm.email) {
      toast.error('Keine E-Mail-Adresse konfiguriert')
      return
    }

    setSendingEmail(msg.message_id)
    try {
      await voicemailApi.resendEmail(selectedVm.id, msg.message_id)
      toast.success('E-Mail wurde versendet')
    } catch {
      toast.error('Fehler beim Versenden')
    } finally {
      setSendingEmail(null)
    }
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('de-DE')
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Voicemail</h4>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Mailbox</th>
              <th>Nebenstelle</th>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : voicemails.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Voicemails konfiguriert</td></tr>
            ) : (
              voicemails.map(vm => (
                <tr key={vm.id}>
                  <td><strong>{vm.mailbox}</strong></td>
                  <td>{vm.extension?.extension || '-'}</td>
                  <td>{vm.fullname || vm.extension?.name || '-'}</td>
                  <td>{vm.email || <span className="text-muted">-</span>}</td>
                  <td><Badge bg={vm.is_active ? 'success' : 'secondary'}>{vm.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-info" size="sm" className="me-1" onClick={() => openMessagesModal(vm)} title="Nachrichten anzeigen">
                      <i className="bi bi-inbox-fill"></i>
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => openSettingsModal(vm)}>
                      <i className="bi bi-gear"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Settings Modal */}
      <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Voicemail-Einstellungen: {selectedVm?.mailbox}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSettingsSubmit}>
          <Modal.Body>
            <Card className="mb-3">
              <Card.Header>Zugang</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Neues Passwort</Form.Label>
                      <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Leer lassen = nicht ändern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>E-Mail</Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Für Benachrichtigungen"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>E-Mail-Einstellungen</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anhang-Format</Form.Label>
                      <Form.Select
                        value={formData.email_format}
                        onChange={(e) => setFormData({...formData, email_format: e.target.value})}
                      >
                        {emailFormats.map(f => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3 pt-4">
                      <Form.Check
                        type="switch"
                        id="email_attach"
                        label="Nachricht anhängen"
                        checked={formData.email_attach}
                        onChange={(e) => setFormData({...formData, email_attach: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3 pt-4">
                      <Form.Check
                        type="switch"
                        id="email_delete"
                        label="Nach E-Mail löschen"
                        checked={formData.email_delete}
                        onChange={(e) => setFormData({...formData, email_delete: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Ansagen</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nicht erreichbar</Form.Label>
                      <Form.Select
                        value={formData.unavail_greeting_id}
                        onChange={(e) => setFormData({...formData, unavail_greeting_id: e.target.value})}
                      >
                        <option value="">-- Standard --</option>
                        {recordings.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Besetzt</Form.Label>
                      <Form.Select
                        value={formData.busy_greeting_id}
                        onChange={(e) => setFormData({...formData, busy_greeting_id: e.target.value})}
                      >
                        <option value="">-- Standard --</option>
                        {recordings.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Temporär</Form.Label>
                      <Form.Select
                        value={formData.temp_greeting_id}
                        onChange={(e) => setFormData({...formData, temp_greeting_id: e.target.value})}
                      >
                        <option value="">-- Keine --</option>
                        {recordings.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Optionen</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max. Nachrichtenlänge (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.max_message_length}
                        onChange={(e) => setFormData({...formData, max_message_length: parseInt(e.target.value) || 180})}
                        min={10}
                        max={600}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Min. Nachrichtenlänge (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.min_message_length}
                        onChange={(e) => setFormData({...formData, min_message_length: parseInt(e.target.value) || 3})}
                        min={1}
                        max={10}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max. Anzahl Nachrichten</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.max_messages}
                        onChange={(e) => setFormData({...formData, max_messages: parseInt(e.target.value) || 100})}
                        min={10}
                        max={1000}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Form.Check
                      type="switch"
                      id="say_caller_id"
                      label="Anrufer-ID ansagen"
                      checked={formData.say_caller_id}
                      onChange={(e) => setFormData({...formData, say_caller_id: e.target.checked})}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Check
                      type="switch"
                      id="say_duration"
                      label="Dauer ansagen"
                      checked={formData.say_duration}
                      onChange={(e) => setFormData({...formData, say_duration: e.target.checked})}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Check
                      type="switch"
                      id="envelope"
                      label="Umschlag-Info"
                      checked={formData.envelope}
                      onChange={(e) => setFormData({...formData, envelope: e.target.checked})}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Check
                      type="switch"
                      id="operator"
                      label="Vermittlung (0)"
                      checked={formData.operator}
                      onChange={(e) => setFormData({...formData, operator: e.target.checked})}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSettingsModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">Speichern</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Messages Modal */}
      <Modal show={showMessagesModal} onHide={() => setShowMessagesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nachrichten: {selectedVm?.mailbox}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={selectedFolder} onSelect={(k) => k && loadMessages(k)} className="mb-3">
            {folders.map(f => (
              <Tab key={f.value} eventKey={f.value} title={f.label}>
                <Table hover size="sm">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Von</th>
                      <th>Datum</th>
                      <th>Dauer</th>
                      <th className="text-end">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4 text-muted">Keine Nachrichten</td></tr>
                    ) : (
                      messages.map(msg => (
                        <tr key={msg.id}>
                          <td>
                            {msg.is_urgent && <i className="bi bi-exclamation-circle-fill text-danger me-1"></i>}
                            {!msg.is_read && <i className="bi bi-circle-fill text-primary" style={{fontSize: '8px'}}></i>}
                          </td>
                          <td>
                            <strong>{msg.caller_id_name || msg.caller_id || 'Unbekannt'}</strong>
                            {msg.caller_id_name && msg.caller_id && (
                              <span className="text-muted ms-1">({msg.caller_id})</span>
                            )}
                          </td>
                          <td>{formatDate(msg.timestamp)}</td>
                          <td>{formatDuration(msg.duration)}</td>
                          <td className="text-end">
                            <Button
                              variant={playingMsgId === msg.message_id ? "primary" : "outline-success"}
                              size="sm"
                              className="me-1"
                              onClick={() => handlePlayMessage(msg)}
                              title={playingMsgId === msg.message_id ? "Stoppen" : "Abspielen"}
                            >
                              <i className={`bi ${playingMsgId === msg.message_id ? 'bi-stop-fill' : 'bi-play-fill'}`}></i>
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="me-1"
                              onClick={() => handleResendEmail(msg)}
                              disabled={!selectedVm?.email || sendingEmail === msg.message_id}
                              title="Per E-Mail senden"
                            >
                              {sendingEmail === msg.message_id ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="bi bi-envelope"></i>
                              )}
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteMessage(msg)} title="Löschen">
                              <i className="bi bi-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Tab>
            ))}
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMessagesModal(false)}>Schließen</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
